import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Radio,
  Select,
  Space,
} from "antd";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AmountInput, type AmountInputProps } from "@/modules/amount-input";
import { DEFAULT_LOCALE, DISPLAY_DATE_FORMAT } from "@/v1/constants";
import { CompanyType, type IUser } from "@/v1/types";
import { taxRegistrationApi } from "./api";
import {
  federalStateOptions,
  getTaxOfficeName,
  getTaxOfficeOptions,
} from "./finanzamtOptions";
import {
  compactObject,
  getDatePickerFormValue,
  getDatePickerValue,
} from "./formHelpers";
import { KapgAddressFields } from "./KapgAddressFields";
import {
  getArticlesOfAssociationGateIssue,
  getBlockingGateIssues,
  getCashAccountingGateIssue,
  getRepresentativeMissingGateIssue,
  getRepresentativePersonGateIssue,
  getShareholderPersonGateIssue,
  getShareTotal,
  getShareTotalGateIssue,
  KapgGateError,
  type KapgGateIssue,
} from "./kapgGates";
import { copyRepresentativeIntoShareholder } from "./kapgRepresentativeCopy";
import { KapgRepresentativePicker } from "./KapgRepresentativePicker";
import { TaxRegistrationAttachments } from "./TaxRegistrationAttachments";
import {
  AlignedFormLink,
  AlignedFormRow,
  InfoMessage,
  InlineForm,
  InlineFormActions,
  ReadOnlyAnswer,
  RepeatingEntry,
  RepeatingEntryHeader,
  Section,
  SectionTitle,
  TextAreaCounterFix,
} from "./styles";
import {
  TaxRegistrationAddressType,
  TaxRegistrationEstimatedBalanceType,
  TaxRegistrationLegalForm,
  TaxRegistrationRegisterType,
  TaxRegistrationShareholderType,
  TaxRegistrationTaxCalculationMethod,
  type AddressAnswers,
  type TaxRegistration,
  type TaxRegistrationKapgAddress,
  type TaxRegistrationQuestions,
  type TaxRegistrationRepresentative,
  type TaxRegistrationShareholder,
} from "./types";
import { nonNegativeAmountRule, xmlSafeTextRule } from "./validation";

export interface TaxRegistrationKapgFormProps {
  user: IUser;
  taxRegistration?: TaxRegistration;
  onSaved: (taxRegistration: TaxRegistration) => Promise<unknown>;
  processActions?: (props: {
    saveBeforeProcessing: () => Promise<TaxRegistration>;
  }) => ReactNode;
  readOnly?: boolean;
}

interface TaxRegistrationKapgFormValues {
  email: string;
  data: TaxRegistrationQuestions;
  /**
   * Not part of `data`, like the EUN form's `businessAddressMode`: a question
   * about the Ort der Geschäftsleitung, not an ELSTER field itself, so it must
   * never reach the payload (SMT-1361).
   */
  managementAddressMode?: "SAME" | "DIFFERENT";
}

/*
 * Neither `min` nor `max` is set on any of these fields. antd applies both by
 * clamping on blur, which rewrites the operator's answer instead of reporting
 * it: a negative Stammkapital would become 0 and a Beteiligung of 150 % would
 * become 100 %, in both cases before the rule or the gate that exists to catch
 * it could fire. The constraints live in `nonNegativeAmountRule` and in
 * `kapgGates`, where they produce a message (SMT-1295).
 */
const wholeEuroInputProps: Pick<
  AmountInputProps,
  "precision" | "allowNegative" | "placeholder" | "style"
> = {
  precision: 0,
  allowNegative: true,
  placeholder: "0",
  style: { width: "100%" },
};
const nominalInputProps: Pick<
  AmountInputProps,
  "precision" | "allowNegative" | "placeholder" | "style"
> = {
  precision: 2,
  allowNegative: true,
  placeholder: "0,00",
  style: { width: "100%" },
};
/* Zeile 165, where a Jahresfehlbetrag is a legal answer, is the same input
 * without the non-negative rule on it. */
const signedEuroInputProps = wholeEuroInputProps;
const headcountInputProps = wholeEuroInputProps;
/* Beteiligung in Prozent gets its own props: no thousands separator, since
 * ELSTER takes a percentage rather than a euro amount (SMT-1358).
 * `allowNegative` stays on here too - the field carries `nonNegativeAmountRule`
 * same as the euro fields, and that rule can only fire on a negative value
 * that actually reaches it; without `allowNegative` the input filter would
 * drop the minus before the rule ever sees it. */
const percentInputProps: Pick<
  AmountInputProps,
  "precision" | "allowNegative" | "placeholder" | "useGrouping" | "style"
> = {
  precision: 2,
  allowNegative: true,
  placeholder: "0,00",
  useGrouping: false,
  style: { width: "100%" },
};

/** ELSTER takes at most three gesetzliche Vertreter and one to six Anteilseigner. */
const MAX_REPRESENTATIVES = 3;
const MAX_SHAREHOLDERS = 6;
const MIN_SHAREHOLDERS = 1;

const NATURAL_ONLY_KEYS = ["firstName", "lastName", "dateOfBirth"] as const;
const LEGAL_ONLY_KEYS = ["companyName", "businessActivity"] as const;

/**
 * Zeile 14 and the Umsatzsteuer section are mutually exclusive: neric drops the
 * whole `vat` object for a vermögensverwaltende Gesellschaft. Hiding the section
 * is not enough, its answers have to go, so listing the keys here keeps the
 * clearing exhaustive rather than relying on a nested `undefined`.
 */
const VAT_KEYS = [
  "expectedTurnover",
  "expectedTurnoverFollowingYear",
  "smallBusiness",
  "smallBusinessWaiver",
  "estimatedBalanceType",
  "estimatedBalanceAmount",
  "monthlyFilingPeriod",
  "taxCalculationMethod",
  "cashAccountingTurnoverLimit",
  "cashAccountingSection148Ao",
] as const;

/**
 * Zeile 194. The form opens on the Ist-Versteuerung, which is what a newly
 * founded company applies for, and on the Verzicht for Zeile 180/181.
 */
const DEFAULT_VAT_ANSWERS = {
  smallBusiness: false,
  taxCalculationMethod: TaxRegistrationTaxCalculationMethod.CASH,
};

/**
 * Both types are explicit on the wire: neric refuses an address that carries
 * both postcodes and a shareholder that mixes the natural and legal fields. The
 * UI already clears the other variant when the type changes; this repeats it on
 * the way out, so a record stored before that existed is repaired on the next
 * save rather than rejected by ERiC.
 */
const normalizeAddress = (
  address?: TaxRegistrationKapgAddress
): TaxRegistrationKapgAddress | undefined => {
  if (!address) return undefined;

  const { type, zip, foreignZip, country, addressExtra, ...rest } = address;
  const addressType = type ?? TaxRegistrationAddressType.DOMESTIC;
  const variant =
    addressType === TaxRegistrationAddressType.FOREIGN
      ? { foreignZip, country, addressExtra }
      : { zip };
  const compacted = compactObject({ ...rest, ...variant });

  return compacted ? { type: addressType, ...compacted } : undefined;
};

/**
 * Whether a stored Ort der Geschäftsleitung answers the question at all, so
 * the form can open on the right side of `managementAddressMode`. Zeilen 8 to
 * 10 are domestic-only (`AddressAnswers`, no `TaxRegistrationKapgAddress`
 * variant), so a plain blank check is enough.
 */
const hasManagementAddressValue = (address?: AddressAnswers): boolean =>
  Boolean(compactObject(address ?? {}));

const normalizeShareholder = (
  shareholder: TaxRegistrationShareholder
): TaxRegistrationShareholder => {
  const {
    type,
    firstName,
    lastName,
    dateOfBirth,
    companyName,
    businessActivity,
    address,
    ...rest
  } = shareholder;
  const shareholderType = type ?? TaxRegistrationShareholderType.NATURAL;
  const variant =
    shareholderType === TaxRegistrationShareholderType.LEGAL
      ? { companyName, businessActivity }
      : { firstName, lastName, dateOfBirth };
  const normalizedAddress = normalizeAddress(address);

  return {
    type: shareholderType,
    ...compactObject({ ...rest, ...variant }),
    ...(normalizedAddress ? { address: normalizedAddress } : {}),
  };
};

const normalizeRepresentative = (
  representative: TaxRegistrationRepresentative
): TaxRegistrationRepresentative | undefined => {
  const { address, ...rest } = representative;
  const normalizedAddress = normalizeAddress(address);
  const compacted = compactObject(rest);

  if (!compacted && !normalizedAddress) return undefined;

  return {
    ...compacted,
    ...(normalizedAddress ? { address: normalizedAddress } : {}),
  };
};

/**
 * Zeile 63. The Rechtsform decides the ELSTER Datenart, so it follows the
 * Mandant record and the operator cannot type it; only the two Rechtsformen
 * this form exists for can reach it.
 */
const LEGAL_FORM_BY_COMPANY_TYPE: Partial<
  Record<CompanyType, TaxRegistrationLegalForm>
> = {
  [CompanyType.GMBH]: TaxRegistrationLegalForm.GMBH,
  [CompanyType.UG]: TaxRegistrationLegalForm.UG,
};

const buildInitialQuestions = (
  user: IUser,
  taxRegistration?: TaxRegistration
): TaxRegistrationQuestions => {
  const stored = taxRegistration?.questions ?? {};
  const mandantBusinessAddress = user.kontaxUser?.businessAddresses?.[0];

  return {
    ...stored,
    company: {
      ...stored.company,
      name: stored.company?.name ?? user.legalName ?? undefined,
      legalForm:
        stored.company?.legalForm ??
        (user.companyType
          ? LEGAL_FORM_BY_COMPANY_TYPE[user.companyType]
          : undefined),
      activityDescription:
        stored.company?.activityDescription ?? user.businessPurpose,
      businessAddress:
        stored.company?.businessAddress ??
        (mandantBusinessAddress
          ? {
              street: mandantBusinessAddress.street,
              zip: mandantBusinessAddress.postCode,
              city: mandantBusinessAddress.city,
            }
          : undefined),
      startDate:
        stored.company?.startDate ??
        user.kontaxUser?.businessStartDate?.slice(0, 10),
    },
    vat: { ...DEFAULT_VAT_ANSWERS, ...stored.vat },
    representatives: stored.representatives ?? [],
    // ELSTER needs at least one Anteilseigner, so the form opens with an empty
    // one instead of asking the operator to add the row first.
    shareholders: stored.shareholders?.length
      ? stored.shareholders
      : [
          {
            type: TaxRegistrationShareholderType.NATURAL,
            address: { type: TaxRegistrationAddressType.DOMESTIC },
          },
        ],
  };
};

/**
 * The FsE questionnaire for Kapitalgesellschaften (GmbH and UG), ELSTER Datenart
 * `FsE_KapG`. Deliberately a separate component from the Einzelunternehmer form:
 * the two share only the Finanzamt step, and every other section differs in
 * fields, Zeilen and validation rules (SMT-1294).
 */
export const TaxRegistrationKapgForm = ({
  user,
  taxRegistration,
  onSaved,
  processActions,
  readOnly = false,
}: TaxRegistrationKapgFormProps) => {
  const { t } = useTranslation("TaxRegistration");
  const [form] = Form.useForm<TaxRegistrationKapgFormValues>();
  const federalState = Form.useWatch(["data", "taxNumber", "state"], form);
  const shareholders = Form.useWatch(["data", "shareholders"], form) as
    | TaxRegistrationShareholder[]
    | undefined;
  const representatives = Form.useWatch(["data", "representatives"], form) as
    | TaxRegistrationRepresentative[]
    | undefined;
  const managementAddressMode = Form.useWatch("managementAddressMode", form);
  const sameManagementAddress = managementAddressMode === "SAME";
  const vermoegensverwaltend = Form.useWatch(
    ["data", "company", "vermoegensverwaltend"],
    form
  );
  const applicationFiled = Form.useWatch(
    [
      "data",
      "company",
      "notarization",
      "commercialRegister",
      "applicationFiled",
    ],
    form
  );
  const registered = Form.useWatch(
    ["data", "company", "notarization", "commercialRegister", "registered"],
    form
  );
  const taxCalculationMethod = Form.useWatch(
    ["data", "vat", "taxCalculationMethod"],
    form
  );
  const cashAccountingTurnoverLimit = Form.useWatch(
    ["data", "vat", "cashAccountingTurnoverLimit"],
    form
  );
  const cashAccountingSection148Ao = Form.useWatch(
    ["data", "vat", "cashAccountingSection148Ao"],
    form
  );
  const [loading, setLoading] = useState(false);
  /*
   * The record as it currently stands on the server, which is not the prop
   * once the form has saved: a new questionnaire gets its id from the first
   * save, and every attachment upload answers with the registration the
   * backend now holds. Keeping it here is what lets an upload and a following
   * save address the same record instead of creating a second one (SMT-1341).
   */
  const [record, setRecord] = useState(taxRegistration);
  const attachments = record?.attachments ?? [];
  /** Set by `onValuesChange`, cleared whenever the fields match the server. */
  const hasUnsavedEdits = useRef(false);
  /**
   * Counts the operator's edits. A save compares it before and after the
   * request, so an answer typed while the request ran keeps the form dirty.
   */
  const editRevision = useRef(0);
  /**
   * The save that is creating a new record, while it runs. An upload started
   * in the meantime waits for this record instead of creating a second one.
   */
  const creatingRecord = useRef<Promise<TaxRegistration>>();
  /** The record the fields on screen were filled from. */
  const appliedRecordId = useRef(taxRegistration?.id);
  /**
   * The dirty signal the snapshot effect and the save compare read. Called
   * from `handleValuesChange` and from anywhere else that changes the fields
   * without going through it, such as `form.setFields`.
   */
  const noteOperatorEdit = () => {
    hasUnsavedEdits.current = true;
    editRevision.current += 1;
  };

  /*
   * The gates are evaluated from what is on screen, not from the last saved
   * record, so the operator sees the answer change as they type. `save` checks
   * the same rules against the payload it is about to send.
   */
  const currentAnswers: TaxRegistrationQuestions = {
    company: { vermoegensverwaltend },
    shareholders,
    representatives,
    vat: {
      taxCalculationMethod,
      cashAccountingTurnoverLimit,
      cashAccountingSection148Ao,
    },
  };
  const shareTotal = getShareTotal(shareholders);
  const shareTotalIssue = getShareTotalGateIssue(currentAnswers);
  const cashAccountingIssue = getCashAccountingGateIssue(currentAnswers);
  const articlesOfAssociationIssue =
    getArticlesOfAssociationGateIssue(attachments);
  const shareholderPersonIssue = getShareholderPersonGateIssue(currentAnswers);
  const representativePersonIssue =
    getRepresentativePersonGateIssue(currentAnswers);
  const representativeMissingIssue =
    getRepresentativeMissingGateIssue(currentAnswers);

  useEffect(() => {
    setRecord(taxRegistration);
    creatingRecord.current = undefined;
  }, [taxRegistration]);

  /*
   * A snapshot of the record fills the fields — unless the operator has typed
   * something into them since the last save. The view reloads the row whenever
   * an attachment changes the status (SMT-1341), and the form is then handed a
   * fresh snapshot of what was last *saved*; filling the fields from it would
   * throw away the answers being written at that moment. The record itself is
   * synced either way, in the effect above, so the Anlagen list, the id and the
   * status stay current.
   *
   * `form.isFieldsTouched()` cannot be the flag: rc-field-form marks a field
   * touched on an external `setFieldsValue` as well, so the effect below would
   * mark the whole form dirty the first time it ran and never fill it again.
   * `onValuesChange` fires for a field the operator changed and not for
   * `setFieldsValue`, which is exactly the signal wanted.
   *
   * A snapshot of a *different* record always wins: the answers on screen
   * belong to the record that is being replaced, not to the one arriving.
   */
  const fillFieldsFrom = useCallback(
    (source: TaxRegistration | undefined) => {
      const initialQuestions = buildInitialQuestions(user, source);

      form.resetFields();
      form.setFieldsValue({
        email: source?.email ?? user.email,
        data: initialQuestions,
        // Zeilen 8 to 10: a stored answer opens the question on DIFFERENT, an
        // absent one on SAME, mirroring the EUN form's businessAddressMode.
        managementAddressMode: hasManagementAddressValue(
          initialQuestions.company?.managementAddress
        )
          ? "DIFFERENT"
          : "SAME",
      });
      hasUnsavedEdits.current = false;
    },
    [form, user]
  );

  useEffect(() => {
    const isSameRecord = appliedRecordId.current === taxRegistration?.id;
    appliedRecordId.current = taxRegistration?.id;
    if (isSameRecord && hasUnsavedEdits.current) return;

    fillFieldsFrom(taxRegistration);
  }, [fillFieldsFrom, taxRegistration]);

  /*
   * Abbrechen discards the unsaved edits (SMT-1401): every field goes back to
   * what the server holds, which is `record` and not the prop (a save without
   * a list refresh and an attachment upload both update `record` only). No
   * request is sent and the row stays open. The button is disabled while a
   * save runs, so this cannot race the edit-revision compare in `save`.
   */
  const handleCancel = () => {
    fillFieldsFrom(record);
  };

  const field = (key: string) => t(`kapg.fields.${key}`);
  const sharedField = (key: string) => t(`fields.${key}`);
  const textRules = (label: string) => [
    xmlSafeTextRule(
      (codePoint) =>
        t("validation.invalidCharacter", { field: label, codePoint }) ?? ""
    ),
  ];
  /*
   * Zeilen 8 to 10. The only fields in this form that carry a real required
   * rule: once the operator answers that the Ort der Geschaeftsleitung differs,
   * a half-filled address would reach ELSTER, and an empty one would silently
   * drop the answer again on save (compactObject removes it). Validation runs
   * on preview and transmission only, so a draft still saves half-typed.
   */
  const requiredTextRules = (label: string) => [
    {
      required: true,
      whitespace: true,
      message: t("validation.fieldRequired", { field: label }) ?? undefined,
    },
    ...textRules(label),
  ];
  const amountRules = [
    nonNegativeAmountRule(t("validation.amountNotNegative") ?? ""),
  ];
  const gateMessage = ({ messageKey, rule }: KapgGateIssue) =>
    String(
      t(`kapg.gates.${messageKey}`, {
        rule,
        total: new Intl.NumberFormat(DEFAULT_LOCALE).format(shareTotal),
      })
    );

  const buildQuestions = (
    formValues: TaxRegistrationKapgFormValues
  ): TaxRegistrationQuestions => {
    const data = (compactObject(formValues.data ?? {}) ??
      {}) as TaxRegistrationQuestions;

    if (data.taxNumber?.taxOfficeCode && !data.taxNumber.taxOfficeName) {
      data.taxNumber.taxOfficeName = getTaxOfficeName(
        data.taxNumber.state,
        data.taxNumber.taxOfficeCode
      );
    }

    data.shareholders = (data.shareholders ?? []).map(normalizeShareholder);

    // Zeile 165 has KapG keys of its own: the Einzelunternehmer profit is
    // non-negative in the API schema, and a Jahresfehlbetrag is legal here. A
    // record that still carries the EUN keys must not send them back.
    if (data.profit) {
      const { expectedCurrentYear, expectedFollowingYear, ...profit } =
        data.profit;
      data.profit = profit;
    }

    if (data.company?.vermoegensverwaltend) {
      delete data.vat;
    } else if (data.vat) {
      // Zeile 180 and 181 are one decision with two mutually exclusive keys, so
      // only the answer that was picked is sent. An unanswered question sends
      // neither: the neric template renders the Verzicht for an absent answer
      // anyway, so writing one would not change the filing, only the record's
      // claim about what the operator answered.
      const { smallBusiness, ...vat } = data.vat;
      if (smallBusiness === undefined) {
        data.vat = vat;
      } else {
        data.vat = smallBusiness
          ? { ...vat, smallBusiness: true }
          : { ...vat, smallBusinessWaiver: true };
      }

      // Zeile 195 and 196 only exist under the Ist-Versteuerung.
      if (
        data.vat.taxCalculationMethod !==
        TaxRegistrationTaxCalculationMethod.CASH
      ) {
        delete data.vat.cashAccountingTurnoverLimit;
        delete data.vat.cashAccountingSection148Ao;
      }
    }

    const representatives = (data.representatives ?? [])
      .map(normalizeRepresentative)
      .filter(
        (representative): representative is TaxRegistrationRepresentative =>
          representative !== undefined
      );
    if (representatives.length) {
      data.representatives = representatives;
    } else {
      delete data.representatives;
    }

    return data;
  };

  const save = async ({
    refreshOnSuccess = true,
    notifyOnSuccess = true,
    rethrowOnError = false,
    validateBeforeSave = false,
  }: {
    refreshOnSuccess?: boolean;
    notifyOnSuccess?: boolean;
    rethrowOnError?: boolean;
    validateBeforeSave?: boolean;
  } = {}): Promise<TaxRegistration | undefined> => {
    try {
      const formValues = validateBeforeSave
        ? await form.validateFields()
        : (form.getFieldsValue(true) as TaxRegistrationKapgFormValues);
      const questions = buildQuestions(formValues);

      // Only preview and transmission are gated, saving a draft never is: an
      // operator has to be able to put down what they have and come back.
      if (validateBeforeSave) {
        const blocking = getBlockingGateIssues(questions, attachments);
        if (blocking.length) {
          throw new KapgGateError(
            blocking.map(gateMessage).join(" "),
            blocking
          );
        }
      }

      setLoading(true);
      const revisionAtSave = editRevision.current;
      const request = taxRegistrationApi.upsertTaxRegistration({
        id: record?.id,
        email: formValues.email.trim(),
        questions,
      });
      if (!record?.id) {
        creatingRecord.current = request;
        // A failed create is forgotten, so the next upload tries again instead
        // of awaiting the same rejection.
        request.catch(() => {
          if (creatingRecord.current === request) {
            creatingRecord.current = undefined;
          }
        });
      }
      const savedTaxRegistration = await request;
      setRecord(savedTaxRegistration);
      /*
       * A new record gets its id from this save, so the snapshot the view sends
       * back afterwards is the same record under a different id than the one
       * the fields were filled from. Without this the effect below would read
       * it as a different record, always win, and reset the fields on the first
       * save of every new questionnaire.
       */
      appliedRecordId.current = savedTaxRegistration.id;
      // What is on screen is now what the server holds, so a snapshot arriving
      // later may fill the fields again. Unless the operator kept typing while
      // the request ran: those answers are not in what was saved.
      if (editRevision.current === revisionAtSave) {
        hasUnsavedEdits.current = false;
      }
      if (notifyOnSuccess) {
        message.success(t("messages.saved"));
      }
      /*
       * Saving refreshes the list but leaves the row expanded (SMT-1360).
       * Collapsing it sent the operator back to the top of the table after
       * every save, and on a questionnaire this long that is the whole way
       * back to where they were. The operator collapses the row by clicking
       * it; Abbrechen discards the unsaved edits and leaves the row open.
       */
      if (refreshOnSuccess) {
        await onSaved(savedTaxRegistration);
      }
      return savedTaxRegistration;
    } catch (error) {
      // A gate error is reported by whoever asked to process, in the words of
      // the action they asked for; reporting it here as well showed the
      // operator two messages for one failure.
      if (
        error instanceof Error &&
        !(error instanceof KapgGateError) &&
        !error.message.includes("validation")
      ) {
        message.error(t("messages.saveError", { error: error.message }));
      }
      if (rethrowOnError) {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const saveBeforeProcessing = async (): Promise<TaxRegistration> => {
    const savedTaxRegistration = await save({
      refreshOnSuccess: false,
      notifyOnSuccess: false,
      rethrowOnError: true,
      validateBeforeSave: true,
    });
    if (!savedTaxRegistration) {
      throw new Error(String(t("messages.saveError", { error: "" })));
    }
    return savedTaxRegistration;
  };

  /*
   * An attachment is stored under the id of its registration, so a record that
   * has never been saved has nothing to upload against. The draft is saved
   * first — ungated, like every other draft save — and the id it comes back
   * with is what the upload uses.
   */
  const ensureRegistrationId = async (): Promise<string> => {
    if (record?.id) return record.id;
    // A save of this new record is already running, e.g. the operator clicked
    // Speichern just before dropping the file. Its record is the one to use.
    if (creatingRecord.current) return (await creatingRecord.current).id;

    const savedTaxRegistration = await save({
      refreshOnSuccess: false,
      notifyOnSuccess: false,
      rethrowOnError: true,
    });
    if (!savedTaxRegistration) {
      throw new Error(String(t("messages.saveError", { error: "" })));
    }
    return savedTaxRegistration.id;
  };

  /*
   * kontax-backend recomputes the status on every attachment change: a KapG
   * record only reaches READY once one is attached, and drops back to DRAFT
   * when the last one goes. The row around the form decides from that status
   * whether the record may be transmitted, so a change of it has to reach the
   * caller; anything else stays local, because a reload would throw away
   * whatever the operator has typed since the last save.
   */
  const handleAttachmentsChange = (updated: TaxRegistration) => {
    setRecord(updated);
    if (updated.status !== record?.status) {
      void onSaved(updated);
    }
  };

  const handleValuesChange = (
    changedValues: Partial<TaxRegistrationKapgFormValues>
  ) => {
    // Only the operator's own edits reach this callback, which is what makes it
    // the dirty flag the snapshot effect reads.
    noteOperatorEdit();

    if (changedValues.data?.taxNumber?.state !== undefined) {
      form.setFieldsValue({
        data: {
          taxNumber: { taxOfficeName: undefined, taxOfficeCode: undefined },
        },
      });
    }

    // Zeile 14: the VAT answers go with the section, they are not just hidden.
    if (changedValues.data?.company?.vermoegensverwaltend === true) {
      form.setFields(
        VAT_KEYS.map((key) => ({
          name: ["data", "vat", key],
          value: undefined,
        }))
      );
    }
    if (changedValues.data?.company?.vermoegensverwaltend === false) {
      form.setFields(
        Object.entries(DEFAULT_VAT_ANSWERS).map(([key, value]) => ({
          name: ["data", "vat", key],
          value,
        }))
      );
    }

    // Zeile 194: the two reasons only exist under the Ist-Versteuerung.
    if (
      changedValues.data?.vat?.taxCalculationMethod ===
      TaxRegistrationTaxCalculationMethod.ACCRUAL
    ) {
      form.setFields([
        {
          name: ["data", "vat", "cashAccountingTurnoverLimit"],
          value: undefined,
        },
        {
          name: ["data", "vat", "cashAccountingSection148Ao"],
          value: undefined,
        },
      ]);
    }

    // Zeilen 8 to 10: SAME means the Ort der Geschäftsleitung is not answered
    // at all, so the preview falls back to the Geschäftsanschrift. Clearing
    // the fields is what keeps `compactObject` dropping the whole
    // `managementAddress` object on save instead of sending an empty one.
    if (changedValues.managementAddressMode === "SAME") {
      form.setFields(
        (["street", "zip", "city"] as const).map((key) => ({
          name: ["data", "company", "managementAddress", key],
          value: undefined,
        }))
      );
    }
  };

  return (
    <InlineForm>
      <Form
        disabled={readOnly}
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        <Section>
          <SectionTitle>{t("sections.taxNumber")}</SectionTitle>
          <AlignedFormRow gutter={16}>
            <Col span={24} style={{ marginBottom: 16 }}>
              <Checkbox checked onChange={() => undefined}>
                <strong>{sharedField("applyForNewTaxNumber")}</strong>{" "}
                {sharedField("applyForNewTaxNumberGerman")}
              </Checkbox>
            </Col>
            <Col span={8}>
              <Form.Item
                required
                name={["data", "taxNumber", "state"]}
                label={sharedField("federalState")}
              >
                <Select allowClear options={federalStateOptions} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required
                name={["data", "taxNumber", "taxOfficeCode"]}
                label={sharedField("taxOffice")}
              >
                <Select
                  allowClear
                  disabled={!federalState}
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(_, option) => {
                    const selectedOption = Array.isArray(option)
                      ? option[0]
                      : option;
                    form.setFieldsValue({
                      data: {
                        taxNumber: {
                          taxOfficeName:
                            typeof selectedOption?.name === "string"
                              ? selectedOption.name
                              : undefined,
                        },
                      },
                    });
                  }}
                  options={getTaxOfficeOptions(federalState)}
                  placeholder={sharedField("taxOffice")}
                  showSearch
                />
              </Form.Item>
              <Form.Item hidden name={["data", "taxNumber", "taxOfficeName"]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label=" " colon={false}>
                <AlignedFormLink
                  href="https://www.bzst.de/DE/Service/Behoerdenwegweiser/Finanzamtsuche/GemFa/finanzamtsuche_node.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("links.taxOfficeSearch")}
                </AlignedFormLink>
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.general")}</SectionTitle>
          <AlignedFormRow gutter={16}>
            <Col span={12}>
              <Form.Item
                required
                name={["data", "company", "name"]}
                label={field("companyName")}
                rules={textRules(field("companyName"))}
              >
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item required name="email" label={sharedField("email")}>
                <Input
                  data-test="email"
                  maxLength={320}
                  placeholder={sharedField("email")}
                />
              </Form.Item>
            </Col>
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={24}>
              <strong>{t("kapg.groups.businessAddress")}</strong>
            </Col>
            <Col span={10}>
              <Form.Item
                required
                name={["data", "company", "businessAddress", "street"]}
                label={sharedField("street")}
                rules={textRules(sharedField("street"))}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                required
                name={["data", "company", "businessAddress", "zip"]}
                label={sharedField("zip")}
                rules={textRules(sharedField("zip"))}
              >
                <Input maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item
                required
                name={["data", "company", "businessAddress", "city"]}
                label={sharedField("city")}
                rules={textRules(sharedField("city"))}
              >
                <Input maxLength={120} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={16}>
              <TextAreaCounterFix>
                <Form.Item
                  required
                  name={["data", "company", "activityDescription"]}
                  label={field("activityDescription")}
                  rules={[
                    {
                      max: 200,
                      message: t("validation.descriptionTooLong") ?? undefined,
                    },
                    ...textRules(field("activityDescription")),
                  ]}
                >
                  <Input.TextArea maxLength={200} rows={3} showCount />
                </Form.Item>
              </TextAreaCounterFix>
            </Col>
            <Col span={24}>
              <Form.Item
                name={["data", "company", "vermoegensverwaltend"]}
                valuePropName="checked"
              >
                <Checkbox>{field("vermoegensverwaltend")}</Checkbox>
              </Form.Item>
            </Col>
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={16}>
              <Form.Item
                name="managementAddressMode"
                label={field("managementAddressMode")}
              >
                <Select>
                  <Select.Option value="SAME">
                    {t("options.sameAsBusinessAddress")}
                  </Select.Option>
                  <Select.Option value="DIFFERENT">
                    {t("options.differentAddress")}
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </AlignedFormRow>
          {!sameManagementAddress && (
            <AlignedFormRow gutter={16}>
              <Col span={10}>
                <Form.Item
                  required
                  name={["data", "company", "managementAddress", "street"]}
                  label={sharedField("street")}
                  rules={requiredTextRules(sharedField("street"))}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={5}>
                <Form.Item
                  required
                  name={["data", "company", "managementAddress", "zip"]}
                  label={sharedField("zip")}
                  rules={requiredTextRules(sharedField("zip"))}
                >
                  <Input maxLength={20} />
                </Form.Item>
              </Col>
              <Col span={9}>
                <Form.Item
                  required
                  name={["data", "company", "managementAddress", "city"]}
                  label={sharedField("city")}
                  rules={requiredTextRules(sharedField("city"))}
                >
                  <Input maxLength={120} />
                </Form.Item>
              </Col>
            </AlignedFormRow>
          )}
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.representatives")}</SectionTitle>
          <Form.List name={["data", "representatives"]}>
            {(entries, { add, remove }) => (
              <>
                {entries.map((entry) => (
                  <RepeatingEntry
                    data-test={`representative-${entry.name}`}
                    key={entry.key}
                  >
                    <RepeatingEntryHeader>
                      <strong>
                        {t("kapg.labels.representative", {
                          index: entry.name + 1,
                        })}
                      </strong>
                      <Button onClick={() => remove(entry.name)} type="link">
                        {t("kapg.actions.removeRepresentative")}
                      </Button>
                    </RepeatingEntryHeader>
                    <AlignedFormRow gutter={16}>
                      <Col span={6}>
                        <Form.Item
                          name={[entry.name, "salutation"]}
                          label={sharedField("salutation")}
                        >
                          <Select
                            allowClear
                            options={[
                              { label: t("options.salutationMs"), value: "MS" },
                              { label: t("options.salutationMr"), value: "MR" },
                              {
                                label: t("options.notSpecified"),
                                value: "NOT_SPECIFIED",
                              },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[entry.name, "firstName"]}
                          label={sharedField("firstName")}
                          rules={textRules(sharedField("firstName"))}
                        >
                          <Input maxLength={120} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[entry.name, "lastName"]}
                          label={sharedField("lastName")}
                          rules={textRules(sharedField("lastName"))}
                        >
                          <Input maxLength={120} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name={[entry.name, "dateOfBirth"]}
                          label={sharedField("dateOfBirth")}
                          getValueProps={(value?: string) => ({
                            value: getDatePickerValue(value),
                          })}
                          normalize={getDatePickerFormValue}
                        >
                          <DatePicker
                            format={DISPLAY_DATE_FORMAT}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      </Col>
                    </AlignedFormRow>
                    <KapgAddressFields
                      form={form}
                      name={[entry.name, "address"]}
                      path={["data", "representatives", entry.name, "address"]}
                    />
                  </RepeatingEntry>
                ))}
                {entries.length < MAX_REPRESENTATIVES && (
                  <Button
                    onClick={() =>
                      add({
                        address: {
                          type: TaxRegistrationAddressType.DOMESTIC,
                        },
                      })
                    }
                    type="dashed"
                  >
                    {t("kapg.actions.addRepresentative")}
                  </Button>
                )}
              </>
            )}
          </Form.List>
          {representativeMissingIssue && (
            <Alert
              message={gateMessage(representativeMissingIssue)}
              showIcon
              type="error"
            />
          )}
          {representativePersonIssue && (
            <Alert
              message={gateMessage(representativePersonIssue)}
              showIcon
              type="error"
            />
          )}
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.notarization")}</SectionTitle>
          <AlignedFormRow gutter={16}>
            <Col span={8}>
              <Form.Item
                name={["data", "company", "notarization", "contractDate"]}
                label={field("contractDate")}
                getValueProps={(value?: string) => ({
                  value: getDatePickerValue(value),
                })}
                normalize={getDatePickerFormValue}
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={12}>
              <Form.Item
                name={[
                  "data",
                  "company",
                  "notarization",
                  "commercialRegister",
                  "applicationFiled",
                ]}
                valuePropName="checked"
              >
                <Checkbox>{field("applicationFiled")}</Checkbox>
              </Form.Item>
            </Col>
            {applicationFiled && (
              <Col span={8}>
                <Form.Item
                  name={[
                    "data",
                    "company",
                    "notarization",
                    "commercialRegister",
                    "applicationDate",
                  ]}
                  label={field("applicationDate")}
                  getValueProps={(value?: string) => ({
                    value: getDatePickerValue(value),
                  })}
                  normalize={getDatePickerFormValue}
                >
                  <DatePicker
                    format={DISPLAY_DATE_FORMAT}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            )}
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={12}>
              <Form.Item
                name={[
                  "data",
                  "company",
                  "notarization",
                  "commercialRegister",
                  "registered",
                ]}
                label={field("registered")}
              >
                <Radio.Group>
                  <Radio value>{t("common.yes")}</Radio>
                  <Radio value={false}>{t("common.no")}</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {registered === true && (
              <Col span={8}>
                <Form.Item
                  name={[
                    "data",
                    "company",
                    "notarization",
                    "commercialRegister",
                    "registrationDate",
                  ]}
                  label={field("registrationDate")}
                  getValueProps={(value?: string) => ({
                    value: getDatePickerValue(value),
                  })}
                  normalize={getDatePickerFormValue}
                >
                  <DatePicker
                    format={DISPLAY_DATE_FORMAT}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
            )}
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={10}>
              <Form.Item
                name={[
                  "data",
                  "company",
                  "notarization",
                  "commercialRegister",
                  "court",
                ]}
                label={field("court")}
                rules={textRules(field("court"))}
              >
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={[
                  "data",
                  "company",
                  "notarization",
                  "commercialRegister",
                  "registerType",
                ]}
                label={field("registerType")}
              >
                <Select
                  allowClear
                  options={Object.values(TaxRegistrationRegisterType).map(
                    (value) => ({ label: value, value })
                  )}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={[
                  "data",
                  "company",
                  "notarization",
                  "commercialRegister",
                  "registerNumber",
                ]}
                label={field("registerNumber")}
                rules={textRules(field("registerNumber"))}
              >
                <Input maxLength={30} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.legalForm")}</SectionTitle>
          <AlignedFormRow gutter={16}>
            <Col span={8}>
              <Form.Item label={field("legalForm")}>
                <span>{user.companyType ?? "—"}</span>
              </Form.Item>
              <Form.Item hidden name={["data", "company", "legalForm"]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required
                name={["data", "company", "startDate"]}
                label={field("startDate")}
                getValueProps={(value?: string) => ({
                  value: getDatePickerValue(value),
                })}
                normalize={getDatePickerFormValue}
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.capital")}</SectionTitle>
          <AlignedFormRow gutter={16}>
            <Col span={8}>
              <Form.Item
                required
                name={["data", "company", "shareCapital"]}
                label={field("shareCapital")}
                rules={amountRules}
              >
                <AmountInput {...wholeEuroInputProps} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["data", "company", "paidInCapital"]}
                label={field("paidInCapital")}
                rules={amountRules}
              >
                <AmountInput {...wholeEuroInputProps} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.shareholders")}</SectionTitle>
          <Form.List name={["data", "shareholders"]}>
            {(entries, { add, remove }) => (
              <>
                {entries.map((entry) => {
                  const shareholderType =
                    shareholders?.[entry.name]?.type ??
                    TaxRegistrationShareholderType.NATURAL;
                  const isLegalEntity =
                    shareholderType === TaxRegistrationShareholderType.LEGAL;

                  return (
                    <RepeatingEntry
                      data-test={`shareholder-${entry.name}`}
                      key={entry.key}
                    >
                      <RepeatingEntryHeader>
                        <strong>
                          {t("kapg.labels.shareholder", {
                            index: entry.name + 1,
                          })}
                        </strong>
                        <Space>
                          <KapgRepresentativePicker
                            onPick={(index) => {
                              const representative = form.getFieldValue([
                                "data",
                                "representatives",
                                index,
                              ]) as TaxRegistrationRepresentative | undefined;
                              if (!representative) return;
                              const currentShareholder = form.getFieldValue([
                                "data",
                                "shareholders",
                                entry.name,
                              ]) as TaxRegistrationShareholder | undefined;
                              // setFields bypasses onValuesChange, so the
                              // dirty signal has to be set here by hand.
                              noteOperatorEdit();
                              form.setFields([
                                {
                                  name: ["data", "shareholders", entry.name],
                                  value: copyRepresentativeIntoShareholder(
                                    currentShareholder,
                                    representative
                                  ),
                                },
                              ]);
                            }}
                            representatives={representatives}
                            shareholders={shareholders}
                          />
                          {entries.length > MIN_SHAREHOLDERS && (
                            <Button
                              onClick={() => remove(entry.name)}
                              type="link"
                            >
                              {t("kapg.actions.removeShareholder")}
                            </Button>
                          )}
                        </Space>
                      </RepeatingEntryHeader>
                      <AlignedFormRow gutter={16}>
                        <Col span={6}>
                          <Form.Item
                            name={[entry.name, "subscriberNumber"]}
                            label={field("subscriberNumber")}
                            rules={textRules(field("subscriberNumber"))}
                          >
                            <Input maxLength={20} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name={[entry.name, "type"]}
                            label={field("shareholderType")}
                            initialValue={
                              TaxRegistrationShareholderType.NATURAL
                            }
                          >
                            <Select
                              onChange={(value) => {
                                const clearedKeys =
                                  value === TaxRegistrationShareholderType.LEGAL
                                    ? NATURAL_ONLY_KEYS
                                    : LEGAL_ONLY_KEYS;
                                form.setFields(
                                  clearedKeys.map((key) => ({
                                    name: [
                                      "data",
                                      "shareholders",
                                      entry.name,
                                      key,
                                    ],
                                    value: undefined,
                                  }))
                                );
                              }}
                              options={[
                                {
                                  label: t("kapg.options.shareholderNatural"),
                                  value: TaxRegistrationShareholderType.NATURAL,
                                },
                                {
                                  label: t("kapg.options.shareholderLegal"),
                                  value: TaxRegistrationShareholderType.LEGAL,
                                },
                              ]}
                            />
                          </Form.Item>
                        </Col>
                      </AlignedFormRow>
                      {isLegalEntity ? (
                        <AlignedFormRow gutter={16}>
                          <Col span={10}>
                            <Form.Item
                              name={[entry.name, "companyName"]}
                              label={field("shareholderCompanyName")}
                              rules={textRules(field("shareholderCompanyName"))}
                            >
                              <Input maxLength={120} />
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              name={[entry.name, "businessActivity"]}
                              label={field("businessActivity")}
                              rules={textRules(field("businessActivity"))}
                            >
                              <Input maxLength={120} />
                            </Form.Item>
                          </Col>
                        </AlignedFormRow>
                      ) : (
                        <AlignedFormRow gutter={16}>
                          <Col span={8}>
                            <Form.Item
                              name={[entry.name, "firstName"]}
                              label={sharedField("firstName")}
                              rules={textRules(sharedField("firstName"))}
                            >
                              <Input maxLength={120} />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name={[entry.name, "lastName"]}
                              label={sharedField("lastName")}
                              rules={textRules(sharedField("lastName"))}
                            >
                              <Input maxLength={120} />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              name={[entry.name, "dateOfBirth"]}
                              label={sharedField("dateOfBirth")}
                              getValueProps={(value?: string) => ({
                                value: getDatePickerValue(value),
                              })}
                              normalize={getDatePickerFormValue}
                            >
                              <DatePicker
                                format={DISPLAY_DATE_FORMAT}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                          </Col>
                        </AlignedFormRow>
                      )}
                      <KapgAddressFields
                        form={form}
                        name={[entry.name, "address"]}
                        path={["data", "shareholders", entry.name, "address"]}
                        required
                      />
                      <AlignedFormRow gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            required
                            name={[entry.name, "nominal"]}
                            label={field("nominal")}
                            rules={amountRules}
                          >
                            <AmountInput {...nominalInputProps} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            required
                            name={[entry.name, "percentage"]}
                            label={field("percentage")}
                            rules={amountRules}
                          >
                            <AmountInput {...percentInputProps} />
                          </Form.Item>
                        </Col>
                      </AlignedFormRow>
                    </RepeatingEntry>
                  );
                })}
                {entries.length < MAX_SHAREHOLDERS && (
                  <Button
                    onClick={() =>
                      add({
                        type: TaxRegistrationShareholderType.NATURAL,
                        address: { type: TaxRegistrationAddressType.DOMESTIC },
                      })
                    }
                    type="dashed"
                  >
                    {t("kapg.actions.addShareholder")}
                  </Button>
                )}
              </>
            )}
          </Form.List>
          {/* Zeile 67a is shown to the operator and never sent: the ELSTER
              template computes its own total. */}
          <InfoMessage>
            {t("kapg.labels.shareTotal", {
              total: new Intl.NumberFormat(DEFAULT_LOCALE).format(shareTotal),
            })}
          </InfoMessage>
          {shareTotalIssue && (
            <Alert
              message={gateMessage(shareTotalIssue)}
              showIcon
              type={shareTotalIssue.severity === "error" ? "error" : "warning"}
            />
          )}
          {shareholderPersonIssue && (
            <Alert
              message={gateMessage(shareholderPersonIssue)}
              showIcon
              type="error"
            />
          )}
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.fixedAnswers")}</SectionTitle>
          <ReadOnlyAnswer data-test="readOnlyTrust">
            <span>{t("kapg.readOnly.trustLabel")}</span>
            <strong>{t("kapg.readOnly.trustValue")}</strong>
          </ReadOnlyAnswer>
          <ReadOnlyAnswer data-test="readOnlyCashFoundation">
            <span>{t("kapg.readOnly.cashFoundationLabel")}</span>
            <strong>{t("kapg.readOnly.cashFoundationValue")}</strong>
          </ReadOnlyAnswer>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.profit")}</SectionTitle>
          <AlignedFormRow gutter={16} $labelMinHeight="68px">
            <Col span={8}>
              <Form.Item
                required
                name={["data", "profit", "netIncome"]}
                label={field("netIncome")}
              >
                <AmountInput {...signedEuroInputProps} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                required
                name={["data", "profit", "netIncomeFollowingYear"]}
                label={field("netIncomeFollowingYear")}
              >
                <AmountInput {...signedEuroInputProps} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        <Section>
          <SectionTitle>{t("kapg.sections.payroll")}</SectionTitle>
          <AlignedFormRow gutter={16} $labelMinHeight="68px">
            <Col span={6}>
              <Form.Item
                name={["data", "payroll", "employeesTotal"]}
                label={field("employeesTotal")}
                rules={amountRules}
              >
                <AmountInput {...headcountInputProps} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={["data", "payroll", "employeesShareholders"]}
                label={field("employeesShareholders")}
                rules={amountRules}
              >
                <AmountInput {...headcountInputProps} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name={["data", "payroll", "employeesMinorEmployment"]}
                label={field("employeesMinorEmployment")}
                rules={amountRules}
              >
                <AmountInput {...headcountInputProps} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
          <AlignedFormRow gutter={16}>
            <Col span={8}>
              <Form.Item
                name={["data", "payroll", "paymentStartDate"]}
                label={field("paymentStartDate")}
                getValueProps={(value?: string) => ({
                  value: getDatePickerValue(value),
                })}
                normalize={getDatePickerFormValue}
              >
                <DatePicker
                  format={DISPLAY_DATE_FORMAT}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={["data", "payroll", "expectedWageTax"]}
                label={field("expectedWageTax")}
                rules={amountRules}
              >
                <AmountInput {...wholeEuroInputProps} />
              </Form.Item>
            </Col>
          </AlignedFormRow>
        </Section>

        {!vermoegensverwaltend && (
          <Section>
            <SectionTitle>{t("sections.vat")}</SectionTitle>
            <AlignedFormRow gutter={16} $labelMinHeight="68px">
              <Col span={8}>
                <Form.Item
                  required
                  name={["data", "vat", "expectedTurnover"]}
                  label={field("expectedTurnover")}
                  rules={amountRules}
                >
                  <AmountInput {...wholeEuroInputProps} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  required
                  name={["data", "vat", "expectedTurnoverFollowingYear"]}
                  label={field("expectedTurnoverFollowingYear")}
                  rules={amountRules}
                >
                  <AmountInput {...wholeEuroInputProps} />
                </Form.Item>
              </Col>
            </AlignedFormRow>
            <AlignedFormRow gutter={16}>
              <Col span={24}>
                <Form.Item name={["data", "vat", "smallBusiness"]}>
                  <Radio.Group
                    style={{ display: "flex", flexDirection: "column", gap: 8 }}
                  >
                    <Radio value>{field("smallBusiness")}</Radio>
                    <Radio value={false}>{field("smallBusinessWaiver")}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </AlignedFormRow>
            <AlignedFormRow gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={["data", "vat", "estimatedBalanceType"]}
                  label={field("estimatedBalanceType")}
                >
                  <Select
                    allowClear
                    options={[
                      {
                        label: t("options.vatPayment"),
                        value: TaxRegistrationEstimatedBalanceType.PAYMENT,
                      },
                      {
                        label: t("options.vatRefund"),
                        value: TaxRegistrationEstimatedBalanceType.REFUND,
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  required
                  name={["data", "vat", "estimatedBalanceAmount"]}
                  label={field("estimatedBalanceAmount")}
                  rules={amountRules}
                >
                  <AmountInput {...wholeEuroInputProps} />
                </Form.Item>
              </Col>
            </AlignedFormRow>
            <AlignedFormRow gutter={16}>
              <Col span={24}>
                <Form.Item
                  name={["data", "vat", "monthlyFilingPeriod"]}
                  valuePropName="checked"
                >
                  <Checkbox>{field("monthlyFilingPeriod")}</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={["data", "vat", "taxCalculationMethod"]}
                  label={field("taxCalculationMethod")}
                >
                  <Select
                    options={[
                      {
                        label: t("options.cash"),
                        value: TaxRegistrationTaxCalculationMethod.CASH,
                      },
                      {
                        label: t("options.accrual"),
                        value: TaxRegistrationTaxCalculationMethod.ACCRUAL,
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            </AlignedFormRow>
            {taxCalculationMethod ===
              TaxRegistrationTaxCalculationMethod.CASH && (
              <AlignedFormRow gutter={16}>
                <Col span={24}>
                  <Form.Item
                    name={["data", "vat", "cashAccountingTurnoverLimit"]}
                    valuePropName="checked"
                  >
                    <Checkbox>{field("cashAccountingTurnoverLimit")}</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name={["data", "vat", "cashAccountingSection148Ao"]}
                    valuePropName="checked"
                  >
                    <Checkbox>{field("cashAccountingSection148Ao")}</Checkbox>
                  </Form.Item>
                </Col>
                {cashAccountingIssue && (
                  <Col span={24}>
                    <Alert
                      message={gateMessage(cashAccountingIssue)}
                      showIcon
                      type="error"
                    />
                  </Col>
                )}
              </AlignedFormRow>
            )}
            <ReadOnlyAnswer data-test="readOnlyVatId">
              <span>{t("kapg.readOnly.vatIdLabel")}</span>
              <strong>{t("kapg.readOnly.vatIdValue")}</strong>
            </ReadOnlyAnswer>
          </Section>
        )}

        <Section>
          <SectionTitle>{t("kapg.sections.attachments")}</SectionTitle>
          {/*
           * Zeile 227 states that the Gesellschaftsvertrag is attached, and
           * since SMT-1341 it is: the files below are transmitted with the
           * questionnaire in the ELSTER `Anhaenge` block, which is why the
           * answer is fixed and the section is no longer read-only.
           */}
          <ReadOnlyAnswer data-test="readOnlyArticlesOfAssociation">
            <span>{t("kapg.readOnly.articlesOfAssociationLabel")}</span>
            <strong>{t("kapg.readOnly.articlesOfAssociationValue")}</strong>
          </ReadOnlyAnswer>
          <TaxRegistrationAttachments
            attachments={attachments}
            ensureRegistrationId={ensureRegistrationId}
            onChange={handleAttachmentsChange}
            readOnly={readOnly}
            registrationId={record?.id}
          />
          {!readOnly && articlesOfAssociationIssue && (
            <AlignedFormRow gutter={16} style={{ marginTop: 12 }}>
              <Col span={24}>
                <Alert
                  message={gateMessage(articlesOfAssociationIssue)}
                  showIcon
                  type="error"
                />
              </Col>
            </AlignedFormRow>
          )}
        </Section>
      </Form>
      {!readOnly && (
        <InlineFormActions>
          <Space>
            <Button disabled={loading} onClick={handleCancel}>
              {t("actions.cancel")}
            </Button>
            <Button loading={loading} onClick={() => save()} type="primary">
              {t("actions.save")}
            </Button>
            {processActions?.({ saveBeforeProcessing })}
          </Space>
        </InlineFormActions>
      )}
    </InlineForm>
  );
};
